import { useCallback, useEffect, useRef, useState } from 'react';
import * as pdfjs from 'pdfjs-dist';
import workerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
import type { SignDocumentDetail, SignFieldKind, SignFieldSummary } from '@todo/shared';
import { ApiError } from '../api/client';
import { signApi } from '../api/sign.api';

pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;

/**
 * The placement editor: the PDF's real pages rendered by pdf.js, with one
 * draggable/resizable signature box per drop. Coordinates live as FRACTIONS
 * of the page (top-left origin), so the render zoom never matters - the
 * sealing step multiplies them by the true page size in points.
 *
 * Interaction: pick a recipient chip, CLICK a page to drop their box; drag
 * a box to move it, drag its corner handle to resize, × to remove.
 */

/** A field being edited (no id until saved). */
type DraftField = Omit<SignFieldSummary, 'id' | 'valueText'>;

const PAGE_WIDTH = 760; // render width in CSS px; height follows the page ratio

const CHIP_COLORS = [
  'bg-amber-100 border-amber-400 text-amber-900',
  'bg-emerald-100 border-emerald-400 text-emerald-900',
  'bg-sky-100 border-sky-400 text-sky-900',
  'bg-purple-100 border-purple-400 text-purple-900',
  'bg-rose-100 border-rose-400 text-rose-900',
  'bg-slate-100 border-slate-400 text-slate-900',
];

export function SignFieldEditor({
  document: doc,
  onClose,
  onSent,
}: {
  document: SignDocumentDetail;
  onClose: () => void;
  /** Called after "Save & send" succeeds. */
  onSent: () => void;
}) {
  const [pageCount, setPageCount] = useState(0);
  const [fields, setFields] = useState<DraftField[]>(
    doc.fields.map(({ id: _id, ...f }) => f),
  );
  const [activeRecipient, setActiveRecipient] = useState(doc.recipients[0]?.id ?? '');
  /** What the NEXT click drops: a signature box or a comment box. */
  const [boxKind, setBoxKind] = useState<SignFieldKind>('SIGNATURE');
  const [busy, setBusy] = useState<'save' | 'send' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const pdfRef = useRef<pdfjs.PDFDocumentProxy | null>(null);
  const canvasRefs = useRef(new Map<number, HTMLCanvasElement>());
  /** Live drag/resize state - kept in a ref so pointermove stays cheap. */
  const gesture = useRef<{
    kind: 'move' | 'resize';
    index: number;
    startX: number;
    startY: number;
    orig: DraftField;
    pageEl: HTMLElement;
  } | null>(null);

  const colorOf = useCallback(
    (recipientId: string) => {
      const i = doc.recipients.findIndex((r) => r.id === recipientId);
      return CHIP_COLORS[i >= 0 ? i % CHIP_COLORS.length : CHIP_COLORS.length - 1];
    },
    [doc.recipients],
  );

  // Load the PDF once, then render each page onto its canvas.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const bytes = await signApi.fileBytes(doc.id);
        const pdf = await pdfjs.getDocument({ data: bytes }).promise;
        if (cancelled) return;
        pdfRef.current = pdf;
        setPageCount(pdf.numPages);
      } catch {
        setError('Could not load the PDF.');
      }
    })();
    return () => { cancelled = true; };
  }, [doc.id]);

  // Render pages when their canvases exist.
  useEffect(() => {
    const pdf = pdfRef.current;
    if (!pdf || pageCount === 0) return;
    let cancelled = false;
    void (async () => {
      for (let n = 1; n <= pdf.numPages; n++) {
        const canvas = canvasRefs.current.get(n);
        if (!canvas || cancelled) continue;
        const page = await pdf.getPage(n);
        const base = page.getViewport({ scale: 1 });
        const scale = PAGE_WIDTH / base.width;
        const viewport = page.getViewport({ scale: scale * window.devicePixelRatio });
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        canvas.style.width = `${PAGE_WIDTH}px`;
        canvas.style.height = `${viewport.height / window.devicePixelRatio}px`;
        await page.render({ canvasContext: canvas.getContext('2d')!, viewport }).promise;
      }
    })();
    return () => { cancelled = true; };
  }, [pageCount]);

  function dropBox(page: number, e: React.MouseEvent<HTMLDivElement>) {
    if (!activeRecipient) return;
    // Ignore clicks that land on an existing box (they drag instead).
    if ((e.target as HTMLElement).closest('[data-sign-box]')) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const w = 0.24; // default box ≈ a quarter of the width
    const h = (0.24 * rect.width * 0.33) / rect.height; // ~3:1 aspect
    const x = Math.min(Math.max((e.clientX - rect.left) / rect.width - w / 2, 0), 1 - w);
    const y = Math.min(Math.max((e.clientY - rect.top) / rect.height - h / 2, 0), 1 - h);
    setFields((fs) => [...fs, {
      recipientId: activeRecipient,
      kind: boxKind,
      // A signature is always required; a comment box is optional unless
      // the sender says otherwise, which is the point of having one.
      required: boxKind === 'SIGNATURE',
      valueText: null,
      page, x, y, width: w, height: h,
    }]);
  }

  function beginGesture(
    kind: 'move' | 'resize',
    index: number,
    e: React.PointerEvent,
  ) {
    e.preventDefault();
    e.stopPropagation();
    const pageEl = (e.target as HTMLElement).closest('[data-sign-page]') as HTMLElement;
    if (!pageEl) return;
    gesture.current = {
      kind, index,
      startX: e.clientX, startY: e.clientY,
      orig: fields[index],
      pageEl,
    };
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  }

  function onPointerMove(e: React.PointerEvent) {
    const g = gesture.current;
    if (!g) return;
    const rect = g.pageEl.getBoundingClientRect();
    const dx = (e.clientX - g.startX) / rect.width;
    const dy = (e.clientY - g.startY) / rect.height;
    setFields((fs) =>
      fs.map((f, i) => {
        if (i !== g.index) return f;
        if (g.kind === 'move') {
          return {
            ...f,
            x: Math.min(Math.max(g.orig.x + dx, 0), 1 - f.width),
            y: Math.min(Math.max(g.orig.y + dy, 0), 1 - f.height),
          };
        }
        return {
          ...f,
          width: Math.min(Math.max(g.orig.width + dx, 0.06), 1 - f.x),
          height: Math.min(Math.max(g.orig.height + dy, 0.02), 1 - f.y),
        };
      }),
    );
  }

  function endGesture() { gesture.current = null; }

  async function save(sendAfter: boolean) {
    setBusy(sendAfter ? 'send' : 'save');
    setError(null);
    try {
      await signApi.setFields(doc.id, { fields });
      if (sendAfter) {
        await signApi.send(doc.id);
        onSent();
      } else {
        onClose();
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save the placement.');
    } finally {
      setBusy(null);
    }
  }

  const nameOf = (recipientId: string) =>
    doc.recipients.find((r) => r.id === recipientId)?.displayName ?? '?';

  /**
   * How many SIGNATURE boxes each recipient has. Comment boxes do not
   * count: a comment is somewhere to type, not somewhere to sign, and an
   * envelope whose third signer only has a comment box is exactly the
   * case this guard exists to catch.
   */
  const signatureCount = (recipientId: string) =>
    fields.filter((f) => f.recipientId === recipientId && f.kind === 'SIGNATURE').length;

  /** Recipients still without a signature box, in signing order. */
  const unplaced = doc.recipients
    .filter((r) => signatureCount(r.id) === 0)
    .sort((a, b) => a.signOrder - b.signOrder);
  const canSend = doc.recipients.length > 0 && unplaced.length === 0;

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-slate-100">
      {/* ---- toolbar ---- */}
      <div className="flex flex-wrap items-center gap-3 border-b border-slate-200 bg-white px-5 py-3">
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-slate-900">{doc.title}</p>
          <p className="text-xs text-slate-500">
            Pick a person and a box type, then click the page where it goes.
          </p>
        </div>
        {/* What the next click drops. Two kinds of box, placed the same
            way: a signature the recipient signs, or a comment they may
            type into. */}
        <div className="flex items-center gap-1 rounded-lg border border-slate-300 p-0.5">
          {(['SIGNATURE', 'COMMENT'] as SignFieldKind[]).map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => setBoxKind(k)}
              aria-pressed={boxKind === k}
              className={[
                'rounded-md px-2.5 py-1 text-xs font-semibold transition',
                boxKind === k ? 'bg-slate-800 text-white' : 'text-slate-600 hover:bg-slate-100',
              ].join(' ')}
            >
              {k === 'SIGNATURE' ? 'Signature' : 'Comment'}
            </button>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-1.5">
          {doc.recipients.map((r) => (
            <button
              key={r.id}
              type="button"
              onClick={() => setActiveRecipient(r.id)}
              className={[
                'rounded-full border px-3 py-1 text-xs font-semibold transition',
                colorOf(r.id),
                activeRecipient === r.id ? 'ring-2 ring-slate-900/30' : 'opacity-70 hover:opacity-100',
              ].join(' ')}
            >
              {r.signOrder}. {r.displayName}
              {/* A running tally per person, so the gap is visible while
                  placing rather than only when Send refuses. */}
              <span className={`ml-1.5 text-[10px] font-bold ${
                signatureCount(r.id) === 0 ? 'text-red-600' : 'text-emerald-700'
              }`}>
                {signatureCount(r.id) === 0 ? '0' : `✓${signatureCount(r.id)}`}
              </span>
            </button>
          ))}
        </div>
        <span className="ml-auto flex items-center gap-2">
          {error && <span className="text-xs text-red-600">{error}</span>}
          <button type="button" onClick={onClose}
            className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:bg-slate-50">
            Cancel
          </button>
          <button type="button" disabled={busy !== null} onClick={() => void save(false)}
            className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:bg-slate-50 disabled:opacity-50">
            {busy === 'save' ? 'Saving…' : 'Save placement'}
          </button>
          <button
            type="button"
            disabled={busy !== null || !canSend}
            onClick={() => void save(true)}
            title={canSend ? undefined : `No signature box yet for ${unplaced.map((r) => r.displayName).join(', ')}`}
            className="rounded-lg bg-brand-600 px-3.5 py-1.5 text-xs font-semibold text-white transition hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-50">
            {busy === 'send' ? 'Sending…' : 'Save & send'}
          </button>
        </span>
      </div>

      {/* Says WHO is missing a box rather than just greying out the
          button - "why can't I send this" is the question a disabled
          button on its own always leaves unanswered. */}
      {!canSend && doc.recipients.length > 0 && (
        <p className="border-b border-amber-200 bg-amber-50 px-5 py-2 text-xs text-amber-800">
          Place a signature box for{' '}
          <span className="font-semibold">
            {unplaced.map((r) => `${r.signOrder}. ${r.displayName}`).join(', ')}
          </span>{' '}
          before this can be sent for signature.
        </p>
      )}

      {/* ---- pages ---- */}
      <div className="flex-1 overflow-y-auto py-6" onPointerMove={onPointerMove} onPointerUp={endGesture}>
        {pageCount === 0 && !error && (
          <p className="py-16 text-center text-sm text-slate-500">Loading PDF…</p>
        )}
        <div className="mx-auto space-y-6" style={{ width: PAGE_WIDTH }}>
          {Array.from({ length: pageCount }, (_, i) => i + 1).map((n) => (
            <div
              key={n}
              data-sign-page
              onClick={(e) => dropBox(n, e)}
              className="relative cursor-crosshair rounded border border-slate-300 bg-white shadow-sm"
            >
              <canvas ref={(el) => { if (el) canvasRefs.current.set(n, el); }} className="block" />
              <span className="absolute -top-2.5 right-2 rounded bg-slate-700 px-1.5 text-[10px] font-medium text-white">
                {n}/{pageCount}
              </span>

              {fields.map((f, index) =>
                f.page === n ? (
                  <div
                    key={index}
                    data-sign-box
                    onPointerDown={(e) => beginGesture('move', index, e)}
                    style={{
                      left: `${f.x * 100}%`,
                      top: `${f.y * 100}%`,
                      width: `${f.width * 100}%`,
                      height: `${f.height * 100}%`,
                    }}
                    className={[
                      'absolute flex cursor-move touch-none items-center justify-center rounded border-2 text-[11px] font-semibold',
                      // Dashed = signature, solid = comment. The shape
                      // carries the difference as well as the words, so a
                      // dense page stays readable at a glance.
                      f.kind === 'COMMENT' ? 'border-solid' : 'border-dashed',
                      colorOf(f.recipientId),
                      'bg-opacity-60',
                    ].join(' ')}
                  >
                    <span className="pointer-events-none truncate px-1">
                      {f.kind === 'COMMENT' ? '💬' : '✍'} {nameOf(f.recipientId)}
                    </span>
                    {/* Only a comment box can be optional - a signature
                        box that nobody has to fill is not a signature. */}
                    {f.kind === 'COMMENT' && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setFields((fs) => fs.map((x, i) =>
                            i === index ? { ...x, required: !x.required } : x));
                        }}
                        title={f.required ? 'Required - click to make optional' : 'Optional - click to require'}
                        className="absolute -left-2 -top-2 rounded-full bg-slate-700 px-1.5 text-[9px] font-bold text-white hover:bg-slate-900"
                      >
                        {f.required ? 'REQ' : 'OPT'}
                      </button>
                    )}
                    <button
                      type="button"
                      aria-label="Remove this box"
                      onClick={(e) => {
                        e.stopPropagation();
                        setFields((fs) => fs.filter((_, i) => i !== index));
                      }}
                      className="absolute -right-2 -top-2 grid h-5 w-5 place-items-center rounded-full bg-slate-700 text-[11px] text-white hover:bg-slate-900"
                    >
                      ×
                    </button>
                    <span
                      onPointerDown={(e) => beginGesture('resize', index, e)}
                      className="absolute -bottom-1.5 -right-1.5 h-3.5 w-3.5 cursor-nwse-resize rounded-sm border border-white bg-slate-600"
                    />
                  </div>
                ) : null,
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
